import L from 'leaflet';
import { ArrowLeft, ArrowRight } from 'react-feather';
import * as React from 'react';
import { config } from '../config';
import 'leaflet.heat';

import { useMapElement } from '../hooks/useMapElement';
import { ChoiceProps, RadioChoice } from './RadioChoice';
import { getIntervalSlice, getSliceCount } from './heatmapUtils';
import { Card } from './Card';
import { CardHeading } from './CardHeading';

const tileSetTemplate = `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}{r}.png?access_token=${config.mapboxToken}`;

interface TimestampedPoint {
  lat: number;
  lng: number;
  timestamp: Date;
}

export const Heatmap: React.FC<{
  points: TimestampedPoint[];
}> = ({ points }) => {
  const mapContainer = React.useRef<HTMLDivElement>(null);
  const [slice, setSlice] = React.useState(0);
  const [displayType, setDisplayType] = React.useState<'full' | 'slice'>(
    'slice',
  );

  const map = useMapElement(mapContainer, {
    scrollWheelZoom: false,
    zoomControl: false,
    dragging: false,
    doubleClickZoom: false,
  });

  const maxSlice = React.useMemo(() => getSliceCount(points) - 1, [points]);

  const slicedPoints = React.useMemo(
    () => (displayType === 'full' ? points : getIntervalSlice(points, slice)),
    [points, slice, displayType],
  );

  const prevSlice = () => {
    setSlice((s) => (s > 0 ? s - 1 : s));
  };

  const nextSlice = () => {
    setSlice((s) => (s < maxSlice ? s + 1 : s));
  };

  React.useEffect(() => {
    if (map) {
      L.tileLayer(tileSetTemplate, {
        tileSize: 512,
        zoomOffset: -1,
      }).addTo(map);

      const markerGroup = L.featureGroup(
        points.map(({ lat, lng }) => L.marker([lat, lng])),
      );
      map.fitBounds(markerGroup.getBounds(), {
        padding: [15, 15],
      });
    }
  }, [map, points]);

  React.useEffect(() => {
    if (map) {
      const heat = L.heatLayer(
        slicedPoints.map(({ lat, lng }) => [lat, lng, 0.18]),
        { radius: 10 },
      ).addTo(map);

      return () => {
        heat.removeFrom(map);
      };
    }
  }, [slicedPoints, map]);

  const radioOptions: ChoiceProps<'full' | 'slice'>[] = [
    { value: 'slice', children: 'By 10min interval', compact: true },
    { value: 'full', children: 'Full match', compact: true },
  ];

  return (
    <Card className="space-y-4 overflow-hidden change-transform">
      <div ref={mapContainer} className="h-72 -mx-4 -mt-4"></div>

      <CardHeading>Heatmap</CardHeading>
      <RadioChoice
        options={radioOptions}
        value={displayType}
        onChange={setDisplayType}
      ></RadioChoice>
      {displayType === 'slice' ? (
        <div className="flex justify-center items-center space-x-10 py-4 ">
          <button
            onClick={prevSlice}
            disabled={slice === 0}
            className="rounded-full bg-grey-100 p-2 disabled:text-grey-500 text-primary-600"
          >
            <ArrowLeft />
          </button>
          <span className="text-grey-700">
            {slice * 10}' - {(slice + 1) * 10}'
          </span>
          <button
            onClick={nextSlice}
            disabled={slice === maxSlice}
            className="rounded-full bg-grey-100 p-2 disabled:text-grey-500 text-primary-600"
          >
            <ArrowRight />
          </button>
        </div>
      ) : null}
    </Card>
  );
};
